/**
 * Writing chord symbols over the music.
 *
 * A symbol is typed, not picked from a list: `Cmaj7`, `F#m7b5/A`. That is how every
 * notation program takes them and how every musician writes them, and it is why the model
 * carries one table that reads what was typed and prints it back. What this layer adds is
 * *where*: a symbol goes over the note the user is on, in that note's bar, at the tick
 * that note begins.
 *
 * A symbol already standing on that tick is replaced rather than joined, because two chords
 * on one beat are a mistake in every reading of it, and replacing is what a user who types
 * over a chord means.
 *
 * Nothing here transposes. A part's symbols move with its notes, which is
 * `transpose`'s business, and a chord symbol is written in the part's own written pitch --
 * so a symbol over a B flat trumpet's staff reads in the trumpet's key, as its printed part
 * does.
 */

import type { Command } from "../commands/types";
import { parseChord } from "../document/harmony";
import type { Harmony } from "../document/harmony";
import type { Score } from "../document/types";
import { barOf, coveredBars, covers, rewriteBar, tickInBar } from "./bar-marks";
import { focusLocation } from "./selection";
import type { Selection } from "./selection";

/**
 * Put a chord symbol over the note the selection is on.
 *
 * The text is read here rather than by the caller so that a host can hand over exactly what
 * was typed and show the refusal it gets back: "Cmaj9" and "H7" fail in different ways and
 * a user is owed the difference.
 */
export function set(score: Score, selection: Selection, text: string): Command | string {
  const at = focusLocation(score, selection);
  if (at === null) return "select a note to write the chord over";
  const bar = barOf(score, at);
  if (bar === null) return "that part has no bar there";

  const read = parseChord(text);
  if (typeof read === "string") return read;

  const onset = tickInBar(score, at);
  const harmony: Harmony = { ...read, onset };
  const kept = bar.harmonies.filter((existing) => existing.onset !== onset);
  const harmonies = [...kept, harmony].sort((left, right) => left.onset - right.onset);
  return rewriteBar(at, bar, { harmonies });
}

/** Take away the symbols standing over the selected music. */
export function clear(score: Score, selection: Selection): Command | string {
  const bars = coveredBars(score, selection);
  if (bars.length === 0) return "no note is selected";

  const commands: Command[] = [];
  for (const { at, ranges } of bars) {
    const bar = barOf(score, at);
    if (bar === null) continue;
    const harmonies = bar.harmonies.filter((harmony) => !covers(ranges, harmony.onset));
    if (harmonies.length === bar.harmonies.length) continue;
    commands.push(rewriteBar(at, bar, { harmonies }));
  }

  if (commands.length === 0) return "there is no chord symbol over the selected notes";
  return { kind: "batch", label: "remove the chord symbols", commands };
}

/** The symbol standing exactly where the selection is, which is what a field shows. */
export function at(score: Score, selection: Selection): Harmony | null {
  const focus = focusLocation(score, selection);
  if (focus === null) return null;
  const onset = tickInBar(score, focus);
  return barOf(score, focus)?.harmonies.find((harmony) => harmony.onset === onset) ?? null;
}
